import axios from 'axios';
import { ValidationStatus } from '~/generated/prisma';
import {
  ValidationDetails,
  ValidationJobCostBreakdown,
} from '~/generated/scraper';

export interface CreateValidationJobData {
  emails?: string[];
  phones?: string[];
}

export interface CreateValidationJobResponse {
  taskId: string;
  validationJobId: string;
  status: ValidationStatus;
  estimatedCreditsCost: number;
  emailCount: number;
  phoneCount: number;
}

/**
 * Create a new validation job
 * @param data - Validation job data (emails and/or phones)
 * @returns Promise with validation task details
 */
export const createValidationJobApi = async (
  data: CreateValidationJobData,
): Promise<CreateValidationJobResponse> => {
  const response = await axios.post<{
    success: boolean;
    message: string;
    data: CreateValidationJobResponse;
  }>('/api/validation/create-job', data);
  return response.data.data;
};

/**
 * Get user's validation tasks
 * @param params - Query parameters (status, limit, offset)
 * @returns Promise with validation tasks and total count
 */
export const getValidationTasksApi = async (params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ tasks: ValidationTask[]; total: number }> => {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.append('status', params.status);
  if (params?.limit) searchParams.append('limit', params.limit.toString());
  if (params?.offset) searchParams.append('offset', params.offset.toString());

  const response = await axios.get<{
    success: boolean;
    data: ValidationTask[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }>(`/api/validation/tasks?${searchParams.toString()}`);

  return {
    tasks: response.data.data,
    total: response.data.pagination.total,
  };
};

export interface ValidationTaskDetails {
  task: {
    id: string;
    validationJobId: string;
    emails: string[] | null;
    phones: string[] | null;
    status: ValidationStatus;
    costBreakdown: ValidationJobCostBreakdown;
    creditsCost: number;
    errorMessage: string | null;
    createdAt: string;
    updatedAt: string;
    startedAt: string | null;
    completedAt: string | null;
  };
  contacts: PaginationResponse<{
    id: string;
    value: string;
    type: 'EMAIL' | 'PHONE';
    verified: boolean;
    validationDetails?: ValidationDetails;
  }>;
  stats: {
    total: number;
    valid: number;
    invalid: number;
    successRate: number;
  };
}

/**
 * Get validation task details with contacts
 * @param taskId - Validation task ID
 * @param params - Query parameters (page, limit, type)
 * @returns Promise with validation task details and contacts
 */
export const getValidationTaskDetailsApi = async (
  taskId: string,
  params?: {
    page?: number;
    limit?: number;
    type?: 'EMAIL' | 'PHONE';
  },
): Promise<ValidationTaskDetails> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.limit) searchParams.append('limit', params.limit.toString());
  if (params?.type) searchParams.append('type', params.type);

  const response = await axios.get<{
    success: boolean;
    data: ValidationTaskDetails;
  }>(`/api/validation/tasks/${taskId}?${searchParams.toString()}`);

  return response.data.data;
};

/**
 * Download validation task results from contacts database
 * @param taskId - Validation task ID
 * @param format - Download format ('csv' or 'excel')
 * @param type - Optional contact type filter ('EMAIL' or 'PHONE')
 * @returns Promise with Blob file data
 */
export const downloadValidationTaskApi = async (
  taskId: string,
  format: 'csv' | 'excel',
  type?: 'EMAIL' | 'PHONE',
): Promise<Blob> => {
  const searchParams = new URLSearchParams();
  searchParams.append('format', format);
  if (type) searchParams.append('type', type);

  const response = await axios.get(
    `/api/validation/tasks/${taskId}/download?${searchParams.toString()}`,
    {
      responseType: 'blob',
    },
  );
  return response.data;
};
